import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { supabaseService } from '../../lib/supabase-service';
import { supabase } from '../../lib/supabase';
import { User, Mail, Phone, Shield, Users, Edit2, Save, X, Trash2, ArrowLeft, Upload, Camera } from 'lucide-react';
import CollegeAccessControl from './CollegeAccessControl';
import NotificationTester from '../Admin/NotificationTester';

export default function ProfileManagement() {
  const { family, user, currentUser, refreshFamilyData, kidAllowances } = useFamilyContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [error, setError] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({
    name: '',
    role: 'child',
    avatar: '🧒',
    dateOfBirth: '',
    email: '',
    phone: ''
  });

  // Check if current user is family owner
  // Primary check: currentUser.isOwner (set from family data)
  // Fallback check: directly look up ownership in family.parents data
  const isParent = currentUser?.isParent || 
    (family?.parents && user?.id && 
     family.parents.some(parent => parent.authUserId === user.id && parent.isParent));
  
  // Debug logging
  console.log('ProfileManagement Debug:', {
    currentUser,
    user,
    family: family ? { name: family.name } : null,
    isParent,
    'currentUser.isParent': currentUser?.isParent,
    'user.id': user?.id,
    'family.parents': family?.parents?.map(p => ({ 
      id: p.id, 
      name: p.name, 
      authUserId: p.authUserId, 
      isParent: p.isParent 
    })),
    'fallback_parent_check': family?.parents && user?.id && 
      family.parents.some(parent => parent.authUserId === user.id && parent.isParent)
  });

  const allMembers = [
    ...(family?.parents || []),
    ...(family?.kids || [])
  ];

  const handlePhotoUpload = async (file, memberId = null) => {
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setError('Image size must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    setError('');

    try {
      // Upload image using the existing supabase service
      const result = await supabaseService.uploadImage(file, 'single-family');
      
      if (memberId) {
        // Update existing member's avatar directly in database
        console.log('🔄 Updating member avatar in database:', { memberId, avatarUrl: result.url });
        const { error: memberError } = await supabase
          .from('family_members')
          .update({ avatar: result.url })
          .eq('id', memberId);

        if (memberError) {
          console.error('❌ Error updating member avatar:', memberError);
          throw memberError;
        }

        console.log('✅ Member avatar updated successfully');

        // If this member is currently being edited, update the form too
        if (editingMember === memberId) {
          setEditForm(prev => ({ ...prev, avatar: result.url }));
        }

        // Refresh family data to show the updated avatar
        await refreshFamilyData();
      } else {
        // Update form for new member or current edit
        if (editingMember) {
          setEditForm(prev => ({ ...prev, avatar: result.url }));
        } else {
          // For new member form
          setAddForm(prev => ({ ...prev, avatar: result.url }));
        }
      }
      
    } catch (error) {
      console.error('Error uploading photo:', error);
      setError('Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleEditMember = (member) => {
    setEditingMember(member.id);
    const allowanceData = kidAllowances[member.id] || {};
    console.log('Editing member:', member);
    console.log('Allowance data:', allowanceData);
    console.log('All kidAllowances:', kidAllowances);
    
    setEditForm({
      name: member.name,
      avatar: member.avatar,
      email: member.email || '',
      phone: member.phone || '',
      dateOfBirth: member.dateOfBirth || '',
      jobSchedulerEnabled: allowanceData.job_scheduler_enabled || false,
      hourlyRate: allowanceData.hourly_rate || ''
    });
    setError('');
  };

  const handleSaveChanges = async (memberId) => {
    if (!editForm.name?.trim()) {
      setError('Name is required');
      return;
    }

    setLoading(true);
    setError('');
    
    const member = allMembers.find(m => m.id === memberId);
    console.log('=== SAVE DEBUG ===');
    console.log('memberId:', memberId);
    console.log('member found:', member);
    console.log('editForm:', editForm);
    console.log('==================');

    try {
      // Update family member record
      const { error: memberError } = await supabase
        .from('family_members')
        .update({
          name: editForm.name,
          avatar: editForm.avatar,
          email: editForm.email || null,
          phone: editForm.phone || null,
          date_of_birth: editForm.dateOfBirth || null
        })
        .eq('id', memberId);

      if (memberError) throw memberError;

      // If this member has an auth account and we're updating email, update auth too
      const member = allMembers.find(m => m.id === memberId);
      if (member?.authUserId && editForm.email && editForm.email !== member.email) {
        // Only family owner can update auth email
        if (isParent) {
          const { error: authError } = await supabase.auth.admin.updateUserById(
            member.authUserId,
            { email: editForm.email }
          );
          
          if (authError) {
            console.warn('Could not update auth email:', authError);
            // Don't fail the whole operation, just warn
          }
        }
      }

      // Update job scheduler settings for kids
      // Temporary workaround: check if this is a kid by checking if they're in family.kids array
      const isKid = family?.kids?.some(kid => kid.id === memberId);
      console.log('Role check:', { 
        memberRole: member?.role, 
        isKid, 
        memberInKidsArray: isKid,
        kidIds: family?.kids?.map(k => k.id)
      });
      
      if (member?.role === 'child' || isKid) {
        console.log('Updating job scheduler settings:', {
          kid_id: memberId,
          job_scheduler_enabled: editForm.jobSchedulerEnabled,
          hourly_rate: editForm.hourlyRate
        });

        try {
          // Check if allowance record exists
          console.log('Checking for existing allowance record...');
          const { data: existingAllowance, error: fetchError } = await supabase
            .from('kid_allowances')
            .select('*')
            .eq('kid_id', memberId)
            .single();
          
          console.log('Existing allowance result:', { existingAllowance, fetchError });

          if (existingAllowance) {
            // Update existing record
            console.log('Updating existing allowance record...');
            const { error: allowanceError } = await supabase
              .from('kid_allowances')
              .update({
                job_scheduler_enabled: editForm.jobSchedulerEnabled || false,
                hourly_rate: parseFloat(editForm.hourlyRate) || 0,
                updated_at: new Date().toISOString()
              })
              .eq('kid_id', memberId);

            if (allowanceError) {
              console.error('Could not update job scheduler settings:', allowanceError);
              throw new Error(`Failed to update job scheduler settings: ${allowanceError.message}`);
            }
            console.log('Allowance record updated successfully');
          } else {
            // Create new record
            console.log('Creating new allowance record...');
            const { error: allowanceError } = await supabase
              .from('kid_allowances')
              .insert({
                kid_id: memberId,
                current_balance: 0.00,
                weekly_amount: 0.00,
                enabled: false,
                job_scheduler_enabled: editForm.jobSchedulerEnabled || false,
                hourly_rate: parseFloat(editForm.hourlyRate) || 0
              });

            if (allowanceError) {
              console.error('Could not create allowance record:', allowanceError);
              throw new Error(`Failed to create allowance record: ${allowanceError.message}`);
            }
            console.log('Allowance record created successfully');
          }

          console.log('Job scheduler settings updated successfully');
        } catch (allowanceUpdateError) {
          console.error('Error in allowance update:', allowanceUpdateError);
          throw allowanceUpdateError;
        }
      }

      console.log('About to refresh family data...');
      // Refresh family data
      await refreshFamilyData();
      console.log('Family data refreshed, clearing form...');
      setEditingMember(null);
      setEditForm({});

    } catch (err) {
      console.error('Error updating member:', err);
      setError(err.message || 'Failed to update member');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingMember(null);
    setEditForm({});
    setError('');
  };

  const handleRemoveMember = async (memberId) => {
    const member = allMembers.find(m => m.id === memberId);
    
    if (!member) return;
    
    // Don't allow removing the family owner
    if (member.isParent) {
      setError('Cannot remove the family owner');
      return;
    }

    if (!confirm(`Are you sure you want to remove ${member.name} from the family? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Remove from family_members table
      const { error: removeError } = await supabase
        .from('family_members')
        .delete()
        .eq('id', memberId);

      if (removeError) throw removeError;

      // Optionally: Cancel any pending invitations for this member
      // Optionally: Remove their auth account (requires admin privileges)

      // Refresh family data
      await refreshFamilyData();

    } catch (err) {
      console.error('Error removing member:', err);
      setError(err.message || 'Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!addForm.name?.trim()) {
      setError('Name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Calculate age from date of birth if provided
      const age = addForm.dateOfBirth 
        ? new Date().getFullYear() - new Date(addForm.dateOfBirth).getFullYear()
        : null;

      const { error } = await supabase
        .from('family_members')
        .insert([{
          name: addForm.name,
          role: addForm.role,
          avatar: addForm.avatar,
          date_of_birth: addForm.dateOfBirth || null,
          age: age,
          email: addForm.email || null,
          phone: addForm.phone || null,
          auth_user_id: null, // No account yet
          is_owner: false
        }]);

      if (error) throw error;

      // Reset form and close
      setAddForm({
        name: '',
        role: 'child',
        avatar: '🧒',
        dateOfBirth: '',
        email: '',
        phone: ''
      });
      setShowAddForm(false);

      // Refresh family data
      await refreshFamilyData();

    } catch (err) {
      console.error('Error adding member:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

 if (!isParent) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-yellow-600" />
            <h3 className="font-medium text-yellow-800">Access Restricted</h3>
          </div>
          <p className="mt-2 text-yellow-700">
            Only parents can manage member profiles.
          </p>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-5xl mx-auto py-8 px-6">
        {/* Header */}
        <div className="bg-neutral-50 rounded-2xl shadow-sm border border-stone-100 mb-8">
          <div className="p-6 border-b border-stone-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
   
                <div className="border-l border-stone-200 h-8"></div>
                <Users className="w-6 h-6 text-teal-600" />
                <div>
                  <h1 className="text-3xl font-bold text-stone-900">Profile Management</h1>
                  <p className="mt-1 text-stone-600">
                    Manage family member profiles, contact information, and account settings
                  </p>
                </div>
              </div>

          
              
              {/* Invite Button - Only show for parents */}
              {isParent && (
                <button
                  onClick={() => navigate('/family-invitations')}
                  className="btn-primary flex items-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Send Invite
                </button>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl text-red-800">
            {error}
          </div>
        )}
        <NotificationTester />

        {/* Family Owner Section */}
        <div className="bg-neutral-50 rounded-2xl shadow-sm border border-stone-100 mb-8">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-stone-900 mb-6 flex items-center gap-3">
              <div className="w-10 h-10 bg-action-subtle rounded-full flex items-center justify-center">
                <Shield className="w-5 h-5 text-teal-600" />
              </div>
              Family Owner
            </h2>
            
            {family?.parents?.filter(p => p.isOwner).map(owner => (
              <div key={owner.id} className="p-6 border border-azure-200 bg-teal-50 rounded-xl">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-stone-100 flex items-center justify-center border-2 border-stone-200">
                      {owner.avatar && owner.avatar.startsWith('http') ? (
                        <img 
                          src={owner.avatar} 
                          alt={`${owner.name}'s avatar`} 
                          className="w-full h-full object-cover" 
                        />
                      ) : (
                        <span className="text-xl">{owner.avatar || '👤'}</span>
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-stone-900">{owner.name}</div>
                      <div className="text-sm text-stone-500">Owner • Parent</div>
                      {owner.email && (
                        <div className="text-sm text-stone-600 flex items-center gap-1 mt-1">
                          <Mail className="w-3 h-3" />
                          {owner.email}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {editingMember === owner.id ? (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveChanges(owner.id)}
                        disabled={loading}
                        className="text-indigo-800 hover:text-emerald-800 p-1"
                        title="Save changes"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="text-stone-600 hover:text-stone-800 p-1"
                        title="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleEditMember(owner)}
                      className="text-indigo-600 hover:text-indigo-800 p-1"
                      title="Edit profile"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {editingMember === owner.id && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        Avatar
                      </label>
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-16 rounded-full overflow-hidden bg-stone-100 flex items-center justify-center border-2 border-stone-200">
                          {editForm.avatar && editForm.avatar.startsWith('http') ? (
                            <img 
                              src={editForm.avatar} 
                              alt="Avatar" 
                              className="w-full h-full object-cover" 
                            />
                          ) : (
                            <span className="text-2xl">{editForm.avatar || '👤'}</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) handlePhotoUpload(file);
                            }}
                            className="hidden"
                            id={`avatar-upload-${owner.id}`}
                            disabled={uploadingPhoto}
                          />
                          <label
                            htmlFor={`avatar-upload-${owner.id}`}
                            className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 disabled:opacity-50"
                          >
                            {uploadingPhoto ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                            ) : (
                              <Upload className="w-4 h-4" />
                            )}
                            {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                          </label>
                          <div className="mt-1">
                            <input
                              type="text"
                              value={editForm.avatar && !editForm.avatar.startsWith('http') ? editForm.avatar : ''}
                              onChange={(e) => setEditForm(prev => ({ ...prev, avatar: e.target.value }))}
                              className="w-full px-3 py-1 text-sm border border-stone-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                              placeholder="Or enter emoji: 👩"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={editForm.phone}
                        onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Other Parents Section */}
        {family?.parents?.filter(p => !p.isOwner).length > 0 && (
          <div className="bg-neutral-50 rounded-2xl shadow-sm border border-stone-100 mb-8">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-stone-900 mb-6">Other Parents</h2>
              <div className="space-y-4">
                {family.parents.filter(p => !p.isOwner).map(parent => (
                  <MemberCard
                    key={parent.id}
                    member={parent}
                    role="Parent"
                    editingMember={editingMember}
                    editForm={editForm}
                    loading={loading}
                    onEdit={handleEditMember}
                    onSave={handleSaveChanges}
                    onCancel={handleCancelEdit}
                    onRemove={handleRemoveMember}
                    setEditForm={setEditForm}
                    handlePhotoUpload={handlePhotoUpload}
                    uploadingPhoto={uploadingPhoto}
                    allowanceData={null}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Kids Section */}
        {family?.kids?.length > 0 && (
          <div className="bg-neutral-50 rounded-2xl shadow-sm border border-stone-100 mb-8">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-stone-900 mb-6">Kids</h2>
              <div className="space-y-4">
                {family.kids.map(kid => (
                  <MemberCard
                    key={kid.id}
                    member={kid}
                    role="Child"
                    editingMember={editingMember}
                    editForm={editForm}
                    loading={loading}
                    onEdit={handleEditMember}
                    onSave={handleSaveChanges}
                    onCancel={handleCancelEdit}
                    onRemove={handleRemoveMember}
                    setEditForm={setEditForm}
                    handlePhotoUpload={handlePhotoUpload}
                    uploadingPhoto={uploadingPhoto}
                    allowanceData={kidAllowances[kid.id]}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* College Access Control */}
        {family?.kids?.length > 0 && <CollegeAccessControl />}

        {/* Add Member Section */}
        <div className="bg-neutral-50 rounded-2xl shadow-sm border border-stone-100">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-stone-900">Add Family Member</h2>
              {!showAddForm && (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="bg-indigo-800 text-neutral-50 px-4 py-2.5 rounded-full hover:bg-emerald-700 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg flex items-center gap-2 font-medium"
                >
                  <Users className="w-4 h-4" />
                  Add Member
                </button>
              )}
            </div>

            {showAddForm && (
              <div className="p-6 border border-stone-200 rounded-xl bg-stone-50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={addForm.name}
                      onChange={(e) => setAddForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="Child's name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Role
                    </label>
                    <select
                      value={addForm.role}
                      onChange={(e) => setAddForm(prev => ({ 
                        ...prev, 
                        role: e.target.value,
                        avatar: e.target.value === 'child' ? '🧒' : '👩'
                      }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="child">Child</option>
                      <option value="parent">Parent</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Avatar
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-stone-100 flex items-center justify-center border-2 border-stone-200">
                        {addForm.avatar && addForm.avatar.startsWith('http') ? (
                          <img 
                            src={addForm.avatar} 
                            alt="Avatar" 
                            className="w-full h-full object-cover" 
                          />
                        ) : (
                          <span className="text-2xl">{addForm.avatar || '👤'}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handlePhotoUpload(file).then(() => {
                                // Photo upload sets editForm.avatar, but we need addForm.avatar
                                // We'll handle this in a moment
                              });
                            }
                          }}
                          className="hidden"
                          id="avatar-upload-new"
                          disabled={uploadingPhoto}
                        />
                        <label
                          htmlFor="avatar-upload-new"
                          className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 disabled:opacity-50"
                        >
                          {uploadingPhoto ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                          ) : (
                            <Upload className="w-4 h-4" />
                          )}
                          {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            value={addForm.avatar && !addForm.avatar.startsWith('http') ? addForm.avatar : ''}
                            onChange={(e) => setAddForm(prev => ({ ...prev, avatar: e.target.value }))}
                            className="w-full px-3 py-1 text-sm border border-stone-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            placeholder="Or enter emoji: 🧒"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {addForm.role === 'child' && (
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-1">
                        Date of Birth
                      </label>
                      <input
                        type="date"
                        value={addForm.dateOfBirth}
                        onChange={(e) => setAddForm(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                        className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Email (optional)
                    </label>
                    <input
                      type="email"
                      value={addForm.email}
                      onChange={(e) => setAddForm(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="email@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Phone (optional)
                    </label>
                    <input
                      type="tel"
                      value={addForm.phone}
                      onChange={(e) => setAddForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleAddMember}
                    disabled={loading || !addForm.name.trim()}
                    className="bg-indigo-800 text-neutral-50 px-6 py-2.5 rounded-full hover:bg-emerald-700 transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
                  >
                    <Save className="w-4 h-4" />
                    {loading ? 'Adding...' : 'Add Member'}
                  </button>
                  <button
                    onClick={() => {
                      setShowAddForm(false);
                      setAddForm({
                        name: '',
                        role: 'child',
                        avatar: '🧒',
                        dateOfBirth: '',
                        email: '',
                        phone: ''
                      });
                      setError('');
                    }}
                    className="bg-stone-500 text-neutral-50 px-6 py-2.5 rounded-full hover:bg-stone-600 transition-all duration-200 hover:-translate-y-0.5 flex items-center gap-2 font-medium"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                </div>

                {error && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                    {error}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MemberCard({ 
  member, 
  role, 
  editingMember, 
  editForm, 
  loading, 
  onEdit, 
  onSave, 
  onCancel, 
  onRemove, 
  setEditForm,
  handlePhotoUpload,
  uploadingPhoto,
  allowanceData
}) {
  return (
    <div className="p-6 border border-stone-200 rounded-xl hover:shadow-md transition-all duration-200">
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full overflow-hidden bg-stone-100 flex items-center justify-center border-2 border-stone-200">
            {member.avatar && member.avatar.startsWith('http') ? (
              <img 
                src={member.avatar} 
                alt={`${member.name}'s avatar`} 
                className="w-full h-full object-cover" 
              />
            ) : (
              <span className="text-xl">{member.avatar || '👤'}</span>
            )}
          </div>
          <div>
            <div className="font-medium text-stone-900">{member.name}</div>
            <div className="text-sm text-stone-500">
              {role}
              {member.age && ` • Age ${member.age}`}
              {member.hasAccount ? (
                <span className="text-indigo-800 ml-2">• Has Account</span>
              ) : (
                <span className="text-orange-600 ml-2">• No Account</span>
              )}
              {role === 'Child' && allowanceData?.job_scheduler_enabled && (
                <span className="text-azure-600 ml-2">• Job Scheduler On</span>
              )}
            </div>
            {member.email && (
              <div className="text-sm text-stone-600 flex items-center gap-1 mt-1">
                <Mail className="w-3 h-3" />
                {member.email}
              </div>
            )}
            {member.phone && (
              <div className="text-sm text-stone-600 flex items-center gap-1 mt-1">
                <Phone className="w-3 h-3" />
                {member.phone}
              </div>
            )}
          </div>
        </div>
        
        <div className="flex gap-2">
          {editingMember === member.id ? (
            <>
              <button
                onClick={() => onSave(member.id)}
                disabled={loading}
                className="text-indigo-800 hover:text-emerald-800 p-1"
                title="Save changes"
              >
                <Save className="w-4 h-4" />
              </button>
              <button
                onClick={onCancel}
                className="text-stone-600 hover:text-stone-800 p-1"
                title="Cancel"
              >
                <X className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => onEdit(member)}
                className="text-indigo-600 hover:text-indigo-800 p-1"
                title="Edit profile"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => onRemove(member.id)}
                className="text-red-600 hover:text-red-800 p-1"
                title="Remove from family"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {editingMember === member.id && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Name
            </label>
            <input
              type="text"
              value={editForm.name}
              onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Avatar
            </label>
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-stone-100 flex items-center justify-center border-2 border-stone-200">
                {editForm.avatar && editForm.avatar.startsWith('http') ? (
                  <img 
                    src={editForm.avatar} 
                    alt="Avatar" 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <span className="text-2xl">{editForm.avatar || '👤'}</span>
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handlePhotoUpload(file, member.id);
                  }}
                  className="hidden"
                  id={`avatar-upload-${member.id}`}
                  disabled={uploadingPhoto}
                />
                <label
                  htmlFor={`avatar-upload-${member.id}`}
                  className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 disabled:opacity-50"
                >
                  {uploadingPhoto ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    value={editForm.avatar && !editForm.avatar.startsWith('http') ? editForm.avatar : ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, avatar: e.target.value }))}
                    className="w-full px-3 py-1 text-sm border border-stone-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Or enter emoji: 👧"
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              value={editForm.phone}
              onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {role === 'Child' && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Date of Birth
              </label>
              <input
                type="date"
                value={editForm.dateOfBirth}
                onChange={(e) => setEditForm(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          )}
          
          {/* Job Scheduler Settings for Kids */}
          {role === 'Child' && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Job Scheduler
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editForm.jobSchedulerEnabled || false}
                  onChange={(e) => setEditForm(prev => ({ ...prev, jobSchedulerEnabled: e.target.checked }))}
                  className="w-4 h-4 text-indigo-600 border-stone-300 rounded focus:ring-indigo-500"
                />
                <span className="text-sm text-stone-700">Enable work shift scheduling</span>
              </div>
              
              {editForm.jobSchedulerEnabled && (
                <div className="mt-2">
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Hourly Rate
                  </label>
                  <div className="flex items-center">
                    <span className="text-stone-500 mr-1">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editForm.hourlyRate || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, hourlyRate: e.target.value }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder="0.00"
                    />
                    <span className="text-stone-500 ml-2">/hour</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}